import React, { useState } from "react";
import style from "./Activity.module.scss";
import Avatar from "../../../../components/Avatar/Avatar";
import Button from "../../../../components/new-button";
import FileIcon from "../../../../assets/svg/icons/common/FileIcon";
import ShowDocsModal from "../../../../../components/modals/show-docs-modal/show-docs-modal";

function Note({ note, isReply = false, showReplyButton = true, onReplyClick }) {
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docModalFile, setDocModalFile] = useState(null);
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  
  // Helper to map signal values to type names and badge classes
  const getNoteType = (signal) => {
    // Map notes_type to signal for children notes
    let mappedSignal = signal;
    
    if (!signal && note.notes_type) {
      if (note.notes_type === "agent_note") {
        mappedSignal = "warning";
      } else if (note.notes_type === "pci_note" || note.notes_type === "admin_note" || note.notes_type === "underwriting_note") {
        mappedSignal = "danger";
      } else if (note.notes_type === "merchant_note") {
        mappedSignal = "merchant";
      }
    }
    
    // Now map the signal to the badge class and label
    if (mappedSignal === "warning") {
      return { label: "Agent", class: style.badge_agent };
    } else if (mappedSignal === "danger") {
      return { label: "PCI", class: style.badge_pci };
    } else if (mappedSignal === "underwriting") {
      return { label: "UW", class: style.badge_underwriting };
    } else if (mappedSignal === "merchant") {
      return { label: "Merchant", class: style.badge_merchant };
    } else if (mappedSignal === "success") {
      return { label: "Processor", class: style.badge_agent }; // Using agent badge for processor
    }
    
    // Default to Agent if no match
    return { label: "Agent", class: style.badge_agent };
  };

  const openDocModal = async (file) => {
    const fileType = file.file_name.split(".")[file.file_name.split(".").length - 1];
    const filePath = file.file_path.replaceAll("/", "*");
    setDocModalFile({ type: fileType, title: file.file_name, id: filePath });
    setShowDocsModal(true);
  };

  const handleReplyClick = () => {
    if (onReplyClick) {
      onReplyClick(note);
    }
  };

  // Helper function to get display name for the user
  const getDisplayName = () => {
    if (note.who && note.who.trim()) {
      return note.who;
    }
    
    // If no who field, try to use note_owner
    if (note.note_owner) {
      return note.note_owner;
    }
    
    // If it has a user_id but no name
    if (note.user_id) {
      return note.user_id === 1 ? 'Admin' : `User ${note.user_id}`;
    }
    
    return "Anonymous";
  };

  // Helper function to get formatted date
  const getFormattedDate = () => {
    if (note.created_at) {
      try {
        const date = new Date(note.created_at);
        
        // Format as "12 Oct | 10:12 PM"
        const day = date.getDate();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[date.getMonth()];
        
        let hours = date.getHours();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // convert 0 to 12
        
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return `${day} ${month} | ${hours}:${minutes} ${ampm}`;
      } catch (e) {
        console.error('Error formatting date:', e);
        return 'Recently';
      }
    }
    
    return "";
  };

  // Helper function to get time ago for tooltip
  const getTimeAgo = () => {
    // If created_ago is available from API, use that
    if (note.created_ago) {
      return note.created_ago;
    }
    
    // If no created_ago, calculate a "time ago" value
    if (note.created_at) {
      try {
        const date = new Date(note.created_at);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);
        
        if (seconds < 60) return 'Just now';
        
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
        
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
        
        const days = Math.floor(hours / 24);
        if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
        
        const months = Math.floor(days / 30);
        if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
        
        const years = Math.floor(months / 12);
        return `${years} year${years !== 1 ? 's' : ''} ago`;
      } catch (e) {
        console.error('Error calculating time ago:', e);
        return 'Recently';
      }
    }
    
    return "";
  };

  // Get the note type (signal or notes_type)
  const noteType = getNoteType(note.signal);

  const toggleReplies = () => {
    setRepliesOpen(!repliesOpen);
  };

  return (
    <>
      <div className={`${style.note} ${isReply ? style.reply : ''}`}>
        <Avatar />
        <div className={style.content}>
          <div className={style.note_container}>
            <div className={style.header}>
              <div className={style.user}>
                <h6>{getDisplayName()} to</h6>
                <span
                  className={`${style.badge} ${noteType.class}`}
                >
                  {noteType.label}
                </span>
              </div>
              <div className={style.time} title={getTimeAgo()}>
                {getFormattedDate()}
              </div>
            </div>

            <div className={style.text}>
              <p>{note.content}</p>
            </div>


            <div className={style.selectedFiles} style={{ display: note?.files?.length > 0 ? 'block' : 'none' }}> 
              {note?.files?.map((fileData, index) => (
                <div
                  key={index}
                  className={style.file_item}
                  title={fileData.file_name}
                  onClick={() => openDocModal(fileData)}
                >
                  <FileIcon color="#E03B24" />
                  <div className={style.file_info}>
                    <span className={style.file_name}>{fileData.file_name}</span>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
        {showReplyButton && !isReply && (
          <Button
            variant="ghost"
            size="small"
            onClick={handleReplyClick}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M14.5 3.5C14.4982 5.22335 13.8128 6.87559 12.5942 8.09418C11.3756 9.31278 9.72338 9.99818 8.00003 10H3.2069L5.35378 12.1462C5.4476 12.2401 5.50031 12.3673 5.50031 12.5C5.50031 12.6327 5.4476 12.7599 5.35378 12.8538C5.25996 12.9476 5.13271 13.0003 5.00003 13.0003C4.86735 13.0003 4.7401 12.9476 4.64628 12.8538L1.64628 9.85375C1.59979 9.80731 1.56291 9.75217 1.53775 9.69147C1.51258 9.63077 1.49963 9.56571 1.49963 9.5C1.49963 9.43429 1.51258 9.36923 1.53775 9.30853C1.56291 9.24783 1.59979 9.19269 1.64628 9.14625L4.64628 6.14625C4.7401 6.05243 4.86735 5.99972 5.00003 5.99972C5.13271 5.99972 5.25996 6.05243 5.35378 6.14625C5.4476 6.24007 5.50031 6.36732 5.50031 6.5C5.50031 6.63268 5.4476 6.75993 5.35378 6.85375L3.2069 9H8.00003C9.45821 8.99835 10.8562 8.41835 11.8873 7.38726C12.9184 6.35617 13.4984 4.95818 13.5 3.5C13.5 3.36739 13.5527 3.24021 13.6465 3.14645C13.7402 3.05268 13.8674 3 14 3C14.1326 3 14.2598 3.05268 14.3536 3.14645C14.4473 3.24021 14.5 3.36739 14.5 3.5Z" fill="#3A383F" />
            </svg>
          </Button>
        )}
      </div>

      {showDocsModal && (
        <ShowDocsModal
          isOpen={showDocsModal}
          close={() => setShowDocsModal(false)}
          file={docModalFile}
        />
      )}

      {repliesOpen && (
        <div className={style.replies}>
          {replies.map(reply => (
            <ReplyMessage key={reply.id} {...reply} />
          ))}
          <button onClick={toggleReplies}>Hide replies</button>
        </div>
      )}
    </>
  );
}

export default Note;
